import { useEffect, useMemo, useState } from "react";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { AiOutlineRobot } from "react-icons/ai";
import { ChevronLeft, ChevronRight, Edit, Eye, Globe, MoreVertical, Plus, RefreshCw, Trash2, X } from "react-feather";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import Swal from "sweetalert2";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs";
import ColumnChooser, { ColumnOption, isColVisible, visibleColCount } from "../../../Components/ColumnChooser/ColumnChooser";
import { useColumnPreferences } from "../../../hooks/useColumnPreferences";
import {
  BotFlowAuditLog,
  BotFlowRecord,
  BotFlowStatus,
  addBotFlow,
  deleteBotFlow,
  fetchBotFlowAuditLog,
  fetchBotFlows,
  updateBotFlow,
} from "./store";

const STATUS_BADGE: Record<BotFlowStatus, string> = {
  Active: "bg-light-success text-success",
  Draft: "bg-light-warning text-warning",
  Disabled: "bg-light-danger text-danger",
};

const STATUS_DOT: Record<BotFlowStatus, string> = {
  Active: "#28a745",
  Draft: "#f0ad4e",
  Disabled: "#dc3545",
};

const AVATAR_PALETTE = [
  { bg: "#e7f1ff", color: "#3b82f6" },
  { bg: "#e8f8ee", color: "#22c55e" },
  { bg: "#f3ecfd", color: "#8b5cf6" },
  { bg: "#fdeaea", color: "#ef4444" },
  { bg: "#fff6e5", color: "#f59e0b" },
];

const validationSchema = Yup.object().shape({
  bot_name: Yup.string().required("Name is required"),
  payer_domain: Yup.string().required("Domain is required"),
  flow_status: Yup.string().required("Status is required"),
  classes: Yup.array().of(Yup.string().required("Class is required")),
});

const splitBotName = (botName: string) => {
  const parts = botName.split(/\s*_\s*/);
  if (parts.length > 1) {
    return { title: parts[0], subtitle: parts.slice(1).join(" _ ") };
  }
  return { title: botName, subtitle: "" };
};

const formatRelative = (dateStr: string) => {
  if (!dateStr) return { relative: "—", exact: "" };
  const parsed = moment(dateStr, "YYYY-MM-DD HH:mm:ss");
  if (!parsed.isValid()) return { relative: dateStr, exact: "" };
  const daysDiff = moment().startOf("day").diff(parsed.clone().startOf("day"), "days");
  let relative: string;
  if (daysDiff <= 0) relative = parsed.fromNow();
  else if (daysDiff === 1) relative = "Yesterday";
  else relative = parsed.format("MMM DD, YYYY");
  return { relative, exact: parsed.format("MMM DD, YYYY • hh:mm A") };
};

const isToday = (dateStr: string) => {
  const parsed = moment(dateStr, "YYYY-MM-DD HH:mm:ss");
  return parsed.isValid() && parsed.isSame(moment(), "day");
};

const pillStyle: React.CSSProperties = {
  display: "inline-block",
  fontSize: "12px",
  fontWeight: 400,
  padding: "3px 8px",
  borderRadius: "10px",
  margin: "3px",
  lineHeight: 1.4,
};

const renderAuditValue = (before: any, after: any) => {
  const isArray = Array.isArray(before) || Array.isArray(after);

  if (isArray) {
    const beforeArr: string[] = Array.isArray(before) ? before : [];
    const afterArr: string[] = Array.isArray(after) ? after : [];
    const added = afterArr.filter((c) => !beforeArr.includes(c));
    const removed = beforeArr.filter((c) => !afterArr.includes(c));

    if (!added.length && !removed.length) {
      return <span className="text-muted small">No changes</span>;
    }

    return (
      <div style={{ display: "flex", flexWrap: "wrap", maxWidth: "420px" }}>
        {removed.map((c, i) => (
          <span key={`r-${i}`} style={{ ...pillStyle, background: "#fbe9e9", color: "#c0392b", textDecoration: "line-through" }}>{c}</span>
        ))}
        {added.map((c, i) => (
          <span key={`a-${i}`} style={{ ...pillStyle, background: "#e6f6ec", color: "#1e7e34" }}>{c}</span>
        ))}
      </div>
    );
  }

  if (before === after) return <span className="text-muted small">No changes</span>;
  return (
    <div style={{ maxWidth: "320px", wordBreak: "break-word" }}>
      <div className="text-danger text-decoration-line-through">{before || "—"}</div>
      <div className="text-success">{after || "—"}</div>
    </div>
  );
};

interface BotFlowFormValues {
  bot_name: string;
  payer_domain: string;
  flow_status: BotFlowStatus;
  classes: string[];
}

const BotFlowManager = () => {
  const [data, setData] = useState<BotFlowRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("recent");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedRecord, setSelectedRecord] = useState<BotFlowRecord | null>(null);
  const [saving, setSaving] = useState(false);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRecord, setAuditRecord] = useState<BotFlowRecord | null>(null);
  const [auditLogs, setAuditLogs] = useState<BotFlowAuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const userId = localStorage.getItem("id") || "";
  const username = localStorage.getItem("UserName") || "";

  const { savedColumns, saveColumns } = useColumnPreferences("BotFlowManagerListing");
  const columnOptions: ColumnOption[] = [
    { id: "BotFlow", label: "Bot Flow", locked: true },
    { id: "Domain", label: "Domain" },
    { id: "Classes", label: "Classes" },
    { id: "LastUpdated", label: "Last Updated" },
    { id: "Status", label: "Status" },
    { id: "Actions", label: "Actions", locked: true },
  ];

  const loadData = async () => {
    setLoading(true);
    const result = await fetchBotFlows();
    setData(result);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const domains = useMemo(
    () => Array.from(new Set(data.map((d) => d.payer_domain).filter(Boolean))).sort(),
    [data]
  );

  const filteredSorted = useMemo(() => {
    const term = search.toLowerCase();
    let result = data.filter((record) => {
      const matchesSearch =
        !term ||
        record.bot_name?.toLowerCase().includes(term) ||
        record.payer_domain?.toLowerCase().includes(term);
      const matchesDomain = !domainFilter || record.payer_domain === domainFilter;
      const matchesStatus = !statusFilter || record.flow_status === statusFilter;
      return matchesSearch && matchesDomain && matchesStatus;
    });

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.bot_name.localeCompare(b.bot_name);
        case "domain":
          return a.payer_domain.localeCompare(b.payer_domain);
        case "oldest":
          return a.last_updated.localeCompare(b.last_updated);
        case "recent":
        default:
          return b.last_updated.localeCompare(a.last_updated);
      }
    });

    return result;
  }, [data, search, domainFilter, statusFilter, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [search, domainFilter, statusFilter, sortBy, perPage]);

  const totalCount = filteredSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageData = filteredSorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const stats = useMemo(() => ({
    total: data.length,
    domains: domains.length,
    updatedToday: data.filter((d) => isToday(d.last_updated)).length,
    needsReview: data.filter((d) => d.flow_status === "Draft").length,
  }), [data, domains]);

  const resetFilters = () => {
    setSearch("");
    setDomainFilter("");
    setStatusFilter("");
    setSortBy("recent");
  };

  const openCreate = () => {
    setFormMode("create");
    setSelectedRecord(null);
    setFormOpen(true);
  };

  const openEdit = (record: BotFlowRecord) => {
    setFormMode("edit");
    setSelectedRecord(record);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setSelectedRecord(null);
  };

  const handleSubmit = async (values: BotFlowFormValues) => {
    setSaving(true);
    try {
      const classes = values.classes.map((c) => c.trim()).filter((c) => c.length > 0);

      if (formMode === "create") {
        const response = await addBotFlow({
          bot_name: values.bot_name,
          payer_domain: values.payer_domain,
          flow_status: values.flow_status,
          classes,
          UserId: userId,
          Username: username,
        });
        if (response.success) {
          Swal.fire({ title: "Bot Flow Manager", icon: "success", text: "Bot flow created successfully!", customClass: { confirmButton: "btn btn-success" } });
          closeForm();
          await loadData();
        } else {
          Swal.fire({ title: "Bot Flow Manager", icon: "error", text: response.message || "Failed to create bot flow!", customClass: { confirmButton: "btn btn-danger" } });
        }
        return;
      }

      if (!selectedRecord) return;
      const Before = {
        bot_name: selectedRecord.bot_name,
        payer_domain: selectedRecord.payer_domain,
        flow_status: selectedRecord.flow_status,
        classes: selectedRecord.classes,
      };
      const After = {
        bot_name: values.bot_name,
        payer_domain: values.payer_domain,
        flow_status: values.flow_status,
        classes,
      };
      const response = await updateBotFlow({
        id: selectedRecord.id,
        bot_name: values.bot_name,
        payer_domain: values.payer_domain,
        flow_status: values.flow_status,
        classes,
        UserId: userId,
        Username: username,
        Before,
        After,
      });

      if (response.success) {
        Swal.fire({ title: "Bot Flow Manager", icon: "success", text: "Bot flow updated successfully!", customClass: { confirmButton: "btn btn-success" } });
        closeForm();
        await loadData();
      } else {
        Swal.fire({ title: "Bot Flow Manager", icon: "error", text: response.message || "Failed to update bot flow!", customClass: { confirmButton: "btn btn-danger" } });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record: BotFlowRecord) => {
    const result = await Swal.fire({
      title: "Delete bot flow?",
      text: `You are about to delete "${record.bot_name}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      customClass: { confirmButton: "btn btn-danger", cancelButton: "btn btn-light" },
      buttonsStyling: false,
    });
    if (!result.isConfirmed) return;

    const response = await deleteBotFlow({
      id: record.id,
      bot_name: record.bot_name,
      deletedBy: username,
      deletedById: userId,
    });

    if (response.success) {
      Swal.fire("Success", "Bot flow deleted successfully", "success");
      await loadData();
    } else {
      Swal.fire("Error", response.message || "Failed to delete bot flow", "error");
    }
  };

  const handleAudit = async (record: BotFlowRecord) => {
    setAuditRecord(record);
    setAuditOpen(true);
    setAuditLogs([]);
    setAuditLoading(true);
    const logs = await fetchBotFlowAuditLog(record.id);
    setAuditLogs(logs);
    setAuditLoading(false);
  };

  const closeAudit = () => {
    setAuditOpen(false);
    setAuditRecord(null);
  };

  return (
    <>
      <Breadcrumbs mainTitle="Bot Flow Manager" title="Bot Flow Manager" />

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
        <p className="text-muted mb-0">Manage, review and maintain bot workflows for each payer/domain.</p>
        {/* <Button color="primary" className="d-flex align-items-center gap-2 flex-shrink-0" onClick={openCreate}>
          <Plus size={16} /> Create Bot Flow
        </Button> */}
      </div>

      <div className="bg-white rounded shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Input
            type="text"
            placeholder="Search by Bot Name, Domain..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: "220px", flex: "2 1 220px" }}
          />
          <Input type="select" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} style={{ maxWidth: "180px" }}>
            <option value="">All Domains</option>
            {domains.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Input>
          <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: "160px" }}>
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
            <option value="Disabled">Disabled</option>
          </Input>
          <Input type="select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ maxWidth: "180px" }}>
            <option value="recent">Recently Updated</option>
            <option value="oldest">Oldest Updated</option>
            <option value="name">Name (A-Z)</option>
            <option value="domain">Domain (A-Z)</option>
          </Input>
          <ColumnChooser options={columnOptions} savedColumns={savedColumns} onChange={saveColumns} />
          <Button color="light" className="d-flex align-items-center gap-2" onClick={resetFilters}>
            <RefreshCw size={14} /> Reset
          </Button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <div className="bg-white rounded shadow-sm p-3 h-100 d-flex align-items-center gap-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, background: "#e7f1ff", color: "#3b82f6" }}>
              <AiOutlineRobot size={22} />
            </div>
            <div>
              <div className="text-muted small">Total Bot Flows</div>
              <div className="fs-4 fw-bold">{stats.total}</div>
              <div className="text-muted small">All bot workflows</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="bg-white rounded shadow-sm p-3 h-100 d-flex align-items-center gap-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, background: "#e8f8ee", color: "#22c55e" }}>
              <Globe size={20} />
            </div>
            <div>
              <div className="text-muted small">Domains</div>
              <div className="fs-4 fw-bold">{stats.domains}</div>
              <div className="text-muted small">Unique domains</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="bg-white rounded shadow-sm p-3 h-100 d-flex align-items-center gap-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, background: "#f3ecfd", color: "#8b5cf6" }}>
              <RefreshCw size={20} />
            </div>
            <div>
              <div className="text-muted small">Updated Today</div>
              <div className="fs-4 fw-bold">{stats.updatedToday}</div>
              <div className="text-muted small">Flows updated today</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="bg-white rounded shadow-sm p-3 h-100 d-flex align-items-center gap-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, background: "#fff6e5", color: "#f0ad4e" }}>
              <Edit size={20} />
            </div>
            <div>
              <div className="text-muted small">Needs Review</div>
              <div className="fs-4 fw-bold">{stats.needsReview}</div>
              <div className="text-muted small">Draft flows to review</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr className="text-uppercase small text-muted">
                {isColVisible(columnOptions, savedColumns, "BotFlow") && <th className="px-3 py-3">Bot Flow</th>}
                {isColVisible(columnOptions, savedColumns, "Domain") && <th className="px-3 py-3">Domain</th>}
                {isColVisible(columnOptions, savedColumns, "Classes") && <th className="px-3 py-3">Classes</th>}
                {isColVisible(columnOptions, savedColumns, "LastUpdated") && <th className="px-3 py-3">Last Updated</th>}
                {isColVisible(columnOptions, savedColumns, "Status") && <th className="px-3 py-3">Status</th>}
                {isColVisible(columnOptions, savedColumns, "Actions") && <th className="px-3 py-3 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleColCount(columnOptions, savedColumns)} className="text-center py-4"><Spinner size="sm" /> Loading...</td></tr>
              ) : pageData.length === 0 ? (
                <tr><td colSpan={visibleColCount(columnOptions, savedColumns)} className="text-center py-4">No Bot Flow Data</td></tr>
              ) : pageData.map((row, index) => {
                const { title, subtitle } = splitBotName(row.bot_name);
                const avatar = AVATAR_PALETTE[index % AVATAR_PALETTE.length];
                const { relative, exact } = formatRelative(row.last_updated);
                return (
                  <tr key={row.id} className="border-bottom">
                    {isColVisible(columnOptions, savedColumns, "BotFlow") && <td className="px-3 py-3">
                      <div className="d-flex align-items-center gap-2">
                        <div className="rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 36, height: 36, background: avatar.bg, color: avatar.color }}>
                          <AiOutlineRobot size={18} />
                        </div>
                        <div>
                          <div className="fw-medium">{title}</div>
                          {subtitle && <div className="text-muted small">{subtitle}</div>}
                        </div>
                      </div>
                    </td>}
                    {isColVisible(columnOptions, savedColumns, "Domain") && <td className="px-3 py-3">
                      <a href={`https://${row.payer_domain}`} target="_blank" rel="noreferrer">{row.payer_domain}</a>
                    </td>}
                    {isColVisible(columnOptions, savedColumns, "Classes") && <td className="px-3 py-3">
                      <div>{row.classes_count} Classes</div>
                      <div className="text-muted small">{row.nodes_count} Nodes</div>
                    </td>}
                    {isColVisible(columnOptions, savedColumns, "LastUpdated") && <td className="px-3 py-3">
                      <div>{relative}</div>
                      <div className="text-muted small">{exact}</div>
                    </td>}
                    {isColVisible(columnOptions, savedColumns, "Status") && <td className="px-3 py-3">
                      <span className={`badge rounded-pill ${STATUS_BADGE[row.flow_status]}`}>
                        <span className="d-inline-block rounded-circle me-1" style={{ width: 6, height: 6, background: STATUS_DOT[row.flow_status] }} />
                        {row.flow_status}
                      </span>
                    </td>}
                    {isColVisible(columnOptions, savedColumns, "Actions") && <td className="px-3 py-3 text-center">
                      <UncontrolledDropdown>
                        <DropdownToggle color="light" size="sm" className="border">
                          <MoreVertical size={16} />
                        </DropdownToggle>
                        <DropdownMenu end className="shadow-sm" style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", minWidth: "170px" }}>
                          <DropdownItem style={{ color: "#212529" }} onClick={() => openEdit(row)}><Edit size={14} className="me-2" />Edit</DropdownItem>
                          <DropdownItem style={{ color: "#212529" }} onClick={() => handleAudit(row)}><Eye size={14} className="me-2" />Audit Log</DropdownItem>
                          <DropdownItem divider />
                          <DropdownItem style={{ color: "#dc3545" }} onClick={() => handleDelete(row)}><Trash2 size={14} className="me-2" />Delete</DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center px-3 py-3 border-top gap-2">
          <div className="text-muted small">
            {totalCount === 0
              ? "No results"
              : `Showing ${(currentPage - 1) * perPage + 1} to ${Math.min(currentPage * perPage, totalCount)} of ${totalCount} results`}
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <Label className="mb-0 small text-muted">Rows per page:</Label>
              <Input type="select" bsSize="sm" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} style={{ width: "70px" }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
            <div className="d-flex align-items-center gap-1">
              <Button color="light" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft size={16} />
              </Button>
              <span className="small mx-2">Page {currentPage} of {totalPages}</span>
              <Button color="light" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={formOpen} toggle={closeForm} size="lg" centered>
        <ModalHeader toggle={closeForm}>{formMode === "create" ? "Create Bot Flow" : "Edit Bot Flow"}</ModalHeader>
        <ModalBody>
          <Formik
            enableReinitialize
            initialValues={{
              bot_name: selectedRecord?.bot_name || "",
              payer_domain: selectedRecord?.payer_domain || "",
              flow_status: (selectedRecord?.flow_status || "Draft") as BotFlowStatus,
              classes: selectedRecord?.classes?.length ? selectedRecord.classes : [""],
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values }) => (
              <Form className="needs-validation" noValidate>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Label>Name<span className="text-danger">*</span></Label>
                    <Field name="bot_name" type="text" className="form-control" />
                    <ErrorMessage name="bot_name" component="span" className="text-danger" />
                  </div>
                  <div className="col-md-4">
                    <Label>Domain<span className="text-danger">*</span></Label>
                    <Field name="payer_domain" type="text" className="form-control" />
                    <ErrorMessage name="payer_domain" component="span" className="text-danger" />
                  </div>
                  <div className="col-md-2">
                    <Label>Status<span className="text-danger">*</span></Label>
                    <Field name="flow_status" as="select" className="form-control">
                      <option value="Active">Active</option>
                      <option value="Draft">Draft</option>
                      <option value="Disabled">Disabled</option>
                    </Field>
                    <ErrorMessage name="flow_status" component="span" className="text-danger" />
                  </div>
                  <div className="col-12">
                    <Label>Classes</Label>
                    <FieldArray name="classes">
                      {({ push, remove }) => (
                        <>
                          {values.classes.map((_, index) => (
                            <div key={index} className="mb-2">
                              <div className="d-flex align-items-center">
                                <Field name={`classes[${index}]`} type="text" className="form-control" />
                                <Plus size={16} color="green" className="ms-2 cursor-pointer" onClick={() => push("")} />
                                {values.classes.length > 1 && (
                                  <X size={16} color="red" className="ms-2 cursor-pointer" onClick={() => remove(index)} />
                                )}
                              </div>
                              <ErrorMessage name={`classes[${index}]`} component="span" className="text-danger" />
                            </div>
                          ))}
                        </>
                      )}
                    </FieldArray>
                  </div>
                </div>
                <div className="text-end mt-3">
                  <Button type="submit" color="success" className="me-3" disabled={saving}>
                    {saving ? <Spinner size="sm" /> : "Save"}
                  </Button>
                  <Button type="button" color="danger" onClick={closeForm}>Cancel</Button>
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>

      <Modal isOpen={auditOpen} toggle={closeAudit} size="lg" centered contentClassName="border-0 shadow" style={{ maxWidth: "800px" }}>
        <ModalHeader toggle={closeAudit}>Audit Log for {auditRecord?.bot_name}</ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: "600px", overflow: "auto" }}>
            {auditLoading ? (
              <div className="text-center py-4"><Spinner size="sm" /> Loading...</div>
            ) : auditLogs.length > 0 ? (
              <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "90px" }} />
                  <col />
                  <col style={{ width: "130px" }} />
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: "#f2f2f2" }}>
                    <th className="px-3 py-2 text-start">Date & Time</th>
                    <th className="px-3 py-2 text-start">Action</th>
                    <th className="px-3 py-2 text-start">Changes</th>
                    <th className="px-3 py-2 text-start">By</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((item) => (
                    <tr key={item.id} className="border-bottom align-top">
                      <td className="px-3 py-2">{item.Modified}</td>
                      <td className="px-3 py-2">
                        <span className="badge bg-light text-dark border">{item.Action}</span>
                      </td>
                      <td className="px-3 py-2">
                        {item.Action === "DELETE" ? (
                          <span className="text-danger">
                            {Array.isArray(item.JsonData) ? item.JsonData.map((d: any) => d.value).join(", ") : String(item.JsonData)}
                          </span>
                        ) : (
                          Array.isArray(item.JsonData) && item.JsonData.map((dataItem: any, dataIndex: number) => (
                            <div key={dataIndex} className="mb-2">
                              <div className="fw-medium text-muted">{dataItem.field || "—"}</div>
                              {renderAuditValue(dataItem.before, dataItem.after)}
                            </div>
                          ))
                        )}
                      </td>
                      <td className="px-3 py-2">{item.UserName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-4 text-muted">No audit data available</div>
            )}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default BotFlowManager;
